import { useRef, useState } from "react";
import ScannerInput from "./ScannerInput";
import { Barcode, X as XIcon } from "@phosphor-icons/react";

/**
 * Compact "Scansiona" button that opens a unified single-scan modal.
 * The modal contains a ScannerInput which accepts BOTH USB scanner input (keyboard)
 * AND, on mobile devices, a camera-scan icon that starts the html5-qrcode reader.
 *
 * On first successful scan, the modal closes and onScan(code) is called.
 *
 * Props:
 *   onScan: (code: string) => void   — REQUIRED
 *   title:  string                   — button tooltip
 *   scannerTitle: string             — header shown inside the scan modal
 *   testid: string                   — test id for the trigger button
 *   size:   number                   — icon size in px
 *   className: string                — extra classes on the trigger
 *   label:  string                   — label under the eyebrow inside the modal
 *   placeholder: string              — placeholder for the input
 */
export default function ScanButton({
  onScan,
  title = "Scansiona codice",
  scannerTitle = "Scanner",
  testid,
  size = 18,
  className = "",
  label,
  placeholder = "Scansiona con scanner USB o digita e premi Invio...",
}) {
  const [open, setOpen] = useState(false);
  const consumedRef = useRef(false);

  const handleOpen = () => {
    consumedRef.current = false;
    setOpen(true);
  };

  const handleScan = (code) => {
    if (consumedRef.current) return;
    consumedRef.current = true;
    const clean = (code || "").trim();
    if (clean) onScan(clean);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={handleOpen}
        title={title}
        aria-label={title}
        className={`inline-flex items-center justify-center gap-1.5 px-2.5 border border-neutral-300 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors bg-white text-[#002FA7] ${className}`}
      >
        <Barcode size={size} weight="bold" />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" data-testid={testid ? `${testid}-modal` : undefined}>
          <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Barcode size={18} className="text-[#002FA7]" weight="bold" />
                <h3 className="font-display font-bold text-lg tracking-tight">{scannerTitle}</h3>
              </div>
              <button
                data-testid={testid ? `${testid}-close` : undefined}
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-neutral-100 transition-colors"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-4">
              <ScannerInput
                onScan={handleScan}
                autoFocus
                label={label || scannerTitle}
                placeholder={placeholder}
                testid={testid ? `${testid}-input` : "scan-input"}
              />
              <p className="text-xs text-neutral-500 mt-3">
                Usa uno scanner USB (che invia Invio dopo il codice) oppure, se sei da smartphone, tocca l&apos;icona <span className="font-semibold text-[#002FA7]">fotocamera</span> per acquisire il codice con la camera.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
